<template>
  <div class="contact">
    <section class="ct-section ct-dark-section">
      <div class="container py-5">
        <div class="row justify-content-center">
          <div class="col-md-8 text-center">
            <div class="card-container">
              <div class="card ct-card p-4">
                <div v-if="formSubmitted" class="text-center p-4">
                  <div class="mb-4 text-success">
                    <i class="fas fa-check-circle" style="font-size: 3rem;"></i>
                  </div>
                  <h3 class="mb-3">{{ t('contact.form.thankYou') }}</h3>
                  <p class="mb-4">{{ t('contact.form.thankYouMessage') }}</p>
                  <router-link to="/" class="ct-btn-primary">{{ t('contact.form.returnHome') }}</router-link>
                </div>

                <div v-else-if="formRejected" class="text-center p-4">
                  <div class="mb-4 text-warning">
                    <i class="fas fa-exclamation-triangle" style="font-size: 3rem;"></i>
                  </div>
                  <h3 class="mb-3">Please use your work email</h3>
                  <p class="mb-4">We cannot accept personal or free email addresses for demo requests.</p>
                  <button @click="resetForm" class="ct-btn-primary">Try Again</button>
                </div>

                <form v-else @submit.prevent="submitRequest" class="text-start">
                  <div class="mb-3">
                    <label for="name" class="form-label">{{ t('contact.form.name') }}</label>
                    <input type="text" class="form-control ct-input" id="name" v-model="form.name" required
                           :class="{ 'is-invalid': errors.name }">
                    <div v-if="errors.name" class="invalid-feedback">{{ errors.name }}</div>
                  </div>

                  <div class="mb-3">
                    <label for="company" class="form-label">{{ t('contact.form.company') }}</label>
                    <input type="text" class="form-control ct-input" id="company" v-model="form.company" required
                           :class="{ 'is-invalid': errors.company }">
                    <div v-if="errors.company" class="invalid-feedback">{{ errors.company }}</div>
                  </div>

                  <div class="mb-3">
                    <label for="email" class="form-label">{{ t('contact.form.email') }}</label>
                    <input type="email" class="form-control ct-input" id="email" v-model="form.email" required
                           :class="{ 'is-invalid': errors.email }">
                    <div v-if="errors.email" class="invalid-feedback">{{ errors.email }}</div>
                  </div>

                  <div class="mb-3">
                    <label for="phone" class="form-label">{{ t('contact.form.phone') }}</label>
                    <input type="tel" class="form-control ct-input" id="phone" v-model="form.phone"
                           :class="{ 'is-invalid': errors.phone }">
                    <div v-if="errors.phone" class="invalid-feedback">{{ errors.phone }}</div>
                  </div>

                  <div class="mb-3">
                    <label for="message" class="form-label">{{ t('contact.form.additionalInfo') }}</label>
                    <textarea class="form-control ct-input" id="message" rows="4" v-model="form.message"
                              :class="{ 'is-invalid': errors.message }"></textarea>
                    <div v-if="errors.message" class="invalid-feedback">{{ errors.message }}</div>
                  </div>

                  <div class="mb-3">
                    <label class="form-label">{{ t('contact.form.interests') }}</label>
                    <div class="form-check">
                      <input class="form-check-input" type="checkbox" id="interest-traders"
                             v-model="form.interests.traders">
                      <label class="form-check-label" for="interest-traders">
                        {{ t('contact.form.traders') }}
                      </label>
                    </div>
                    <div class="form-check">
                      <input class="form-check-input" type="checkbox" id="interest-brokers"
                             v-model="form.interests.brokers">
                      <label class="form-check-label" for="interest-brokers">
                        {{ t('contact.form.brokers') }}
                      </label>
                    </div>
                    <div class="form-check">
                      <input class="form-check-input" type="checkbox" id="interest-risk" v-model="form.interests.risk">
                      <label class="form-check-label" for="interest-risk">
                        {{ t('contact.form.risk') }}
                      </label>
                    </div>
                  </div>

                  <div v-if="submissionError" class="alert alert-danger">
                    {{ submissionError }}
                  </div>

                  <div class="text-center">
                    <button type="submit" class="ct-btn-primary" :disabled="submitting">
                      <span v-if="submitting">
                        <i class="fas fa-spinner fa-spin me-2"></i>{{ t('contact.form.submitting') }}
                      </span>
                      <span v-else>{{ t('contact.form.submit') }}</span>
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { useI18n } from 'vue-i18n'
import apiClient from '@/services/api.js';


export default {
  name: 'ContactForm',
  setup() {
    // Access i18n in the Composition API way
    const { t, locale } = useI18n()

    // Export t and locale for use in the template
    return { t, locale }
  },
  data() {
    return {
      form: {
        name: '',
        company: '',
        email: '',
        phone: '',
        message: '',
        interests: {
          traders: false,
          brokers: false,
          risk: false
        }
      },
      errors: {
        name: '',
        company: '',
        email: '',
        phone: '',
        message: ''
      },
      submitting: false,
      formSubmitted: false,
      formRejected: false,
      submissionError: ''
    };
  },
  created() {
    // Check if there are query parameters to pre-fill the form
    const {name, email} = this.$route.query;
    if (name) this.form.name = name;
    if (email) this.form.email = email;
  },
  methods: {
    resetForm() {
      this.form = {
        name: '',
        company: '',
        email: '',
        phone: '',
        message: '',
        interests: {
          traders: false,
          brokers: false,
          risk: false
        }
      };
      this.errors = {
        name: '',
        company: '',
        email: '',
        phone: '',
        message: ''
      };
      this.formRejected = false;
      this.submissionError = '';
    },
    validateForm() {
      let isValid = true;
      this.errors = {
        name: '',
        company: '',
        email: '',
        phone: '',
        message: ''
      };

      if (!this.form.name.trim()) {
        this.errors.name = this.t('errors.required', { field: this.t('contact.form.name') });
        isValid = false;
      }

      if (!this.form.company.trim()) {
        this.errors.company = this.t('errors.required', { field: this.t('contact.form.company') });
        isValid = false;
      }

      if (!this.form.email.trim()) {
        this.errors.email = this.t('errors.required', { field: this.t('contact.form.email') });
        isValid = false;
      } else if (!/^\S+@\S+\.\S+$/.test(this.form.email)) {
        this.errors.email = this.t('errors.email');
        isValid = false;
      }

      return isValid;
    },
    submitRequest() {
      if (!this.validateForm()) return;

      this.submitting = true;
      this.submissionError = '';
      this.formRejected = false;

      // Format interests
      const interests = Object.entries(this.form.interests)
          .filter(([, value]) => value)
          .map(([key]) => this.t(`contact.form.${key}`))
          .join(', ');

      // Create request payload
      const payload = {
        email: this.form.email,
        telephone: this.form.phone || '',
        name: this.form.name || '',
        company: this.form.company || '',
        message: `${this.form.message || ''}\nInterests: ${interests}`,
        reason: 'Website Contact Form'
      };

      // Make API request
      apiClient.post('/contact/', payload)
        // eslint-disable-next-line no-unused-vars
        .then(response => {
          this.submitting = false;
          this.formSubmitted = true;
        })
        .catch(error => {
          this.submitting = false;

          if (error.response && error.response.status === 422) {
            // Rejected email domain
            this.formRejected = true;
          } else {
            // Other error
            this.submissionError = error.response?.data?.error || error.message || this.t('errors.submission');
          }
        });
    },
    getCookie(name) {
      let cookieValue = null;
      if (document.cookie && document.cookie !== '') {
        const cookies = document.cookie.split(';');
        for (let i = 0; i < cookies.length; i++) {
          const cookie = cookies[i].trim();
          if (cookie.substring(0, name.length + 1) === (name + '=')) {
            cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
            break;
          }
        }
      }
      return cookieValue;
    }
  }
};
</script>

<style scoped>
.card-container {
  width: 100%;
  position: relative;
  overflow: visible;
}

.form-check-input:checked {
  background-color: var(--ct-orange);
  border-color: var(--ct-orange);
}

.form-check-input:focus {
  border-color: var(--ct-orange);
  box-shadow: 0 0 0 0.25rem rgba(253, 153, 0, 0.25);
}

.invalid-feedback {
  display: block;
}
</style>